import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

/**
 * Activates optional stitching-service products and keeps their accelerated
 * checkout fields linked to the currently selected parent variant.
 */
class StitchingProducts extends HTMLElement {
  #form;
  #abortController = new AbortController();

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.#form = document.getElementById(this.dataset.formId);
    if (!(this.#form instanceof HTMLFormElement)) return;

    const { signal } = this.#abortController;
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('close', this.#handleDialogClose, { signal });
    });
    this.#form.addEventListener('change', this.#syncParentIds, { signal });
    this.#form.closest('product-form-component')?.addEventListener('cart:update', this.#reset, { signal });
    this.#form.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.#syncParentIds();
    this.#updateTotal();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleChange = (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement) || !checkbox.matches('[data-nested-cart-item]')) return;

    const item = checkbox.closest('[data-nested-cart-item-container]');
    const fields = item?.querySelector('fieldset');
    const supportingInputs = item?.querySelectorAll('[data-nested-cart-item-variant], [data-nested-cart-item-parent]');
    fields?.toggleAttribute('disabled', !checkbox.checked);
    supportingInputs?.forEach((input) => input.toggleAttribute('disabled', !checkbox.checked));
    this.#syncParentIds();
    this.#updateTotal();

    const dialogComponent = item?.querySelector('dialog-component') || item;
    if (checkbox.checked) {
      delete item?.dataset.measurementsSaved;
      requestAnimationFrame(() => dialogComponent?.showDialog?.());
    } else {
      this.#clearItem(item);
      dialogComponent?.closeDialog?.();
    }
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const viewButton = event.target.closest('[data-stitching-products-view]');
    if (viewButton) {
      const item = viewButton.closest('[data-nested-cart-item-container]');
      item?.showDialog?.();
      return;
    }

    const saveButton = event.target.closest('[data-stitching-products-save]');
    if (!saveButton) return;

    const item = saveButton.closest('[data-nested-cart-item-container]');
    const fields = Array.from(item?.querySelectorAll('[data-nested-cart-item-property]') || []);
    const invalidField = fields.find(
      (field) => field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? !field.validity.valid : false
    );
    if (invalidField instanceof HTMLElement) {
      invalidField.reportValidity();
      return;
    }

    item.dataset.measurementsSaved = 'true';
    const savedViewButton = item.querySelector('[data-stitching-products-view]');
    if (savedViewButton instanceof HTMLButtonElement) savedViewButton.hidden = false;
    item?.closeDialog?.();
    this.#updateTotal();
  };

  #handleDialogClose = (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    const item = event.target.closest('[data-nested-cart-item-container]');
    if (!item || item.dataset.measurementsSaved === 'true') return;

    const checkbox = item.querySelector('[data-nested-cart-item]');
    if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
    this.#clearItem(item);
  };

  #clearItem(item) {
    if (!item) return;
    delete item.dataset.measurementsSaved;
    const viewButton = item.querySelector('[data-stitching-products-view]');
    if (viewButton instanceof HTMLButtonElement) viewButton.hidden = true;
    item.querySelector('fieldset')?.setAttribute('disabled', '');
    item.querySelectorAll('[data-nested-cart-item-variant], [data-nested-cart-item-parent]').forEach((input) => {
      input.setAttribute('disabled', '');
    });
    item.querySelectorAll('[data-nested-cart-item-property]').forEach((input) => {
      input.value = '';
    });
    this.#updateTotal();
  }

  #updateTotal = () => {
    const selectedItems = Array.from(this.querySelectorAll('[data-nested-cart-item]:checked'));
    const totalRow = this.querySelector('[data-stitching-products-total-row]');
    if (!totalRow) return;

    const totalElement = this.querySelector('[data-stitching-products-total]');
    totalRow.hidden = selectedItems.length === 0;
    if (selectedItems.length === 0) {
      if (totalElement) totalElement.textContent = '';
      return;
    }

    const serviceTotal = selectedItems.reduce((sum, checkbox) => {
      const item = checkbox.closest('[data-nested-cart-item-container]');
      return sum + Number(item?.dataset.stitchingProductPrice || 0);
    }, 0);
    if (!totalElement) return;

    const total = Number(this.dataset.mainProductPrice || 0) + serviceTotal;
    totalElement.textContent = formatMoney(total, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  };

  #handleVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== String(this.#form?.closest('product-form-component')?.dataset.productId)) return;
    const price = Number(event.detail?.resource?.price);
    if (Number.isFinite(price)) {
      this.dataset.mainProductPrice = String(price);
      this.#updateTotal();
    }
  };

  #syncParentIds = () => {
    const parentId = this.#form?.querySelector('input[name="id"]')?.value || '';
    this.querySelectorAll('[data-nested-cart-item-parent]').forEach((input) => {
      input.value = parentId;
    });
  };

  #reset = (event) => {
    if (event.detail?.data?.source !== 'product-form-component' || event.detail?.data?.didError) return;
    this.querySelectorAll('[data-nested-cart-item]').forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.querySelectorAll('[data-nested-cart-item-container]').forEach((item) => {
      item.closeDialog?.();
      this.#clearItem(item);
    });
  };
}

if (!customElements.get('stitching-products')) {
  customElements.define('stitching-products', StitchingProducts);
}
